import { useState, useEffect, useCallback } from 'react';
import { Calendar, User, MapPin, Clock, PartyPopper, X, ChevronLeft, ChevronRight, Bell } from 'lucide-react';
import getApiUrl from '../utils/getApiUrl';

const API = `${getApiUrl()}/api`;

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};


export default function BanniereNouveauR1({ onClientClick }) {
  const [rdvList, setRdvList] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isMinimized, setIsMinimized] = useState(false);
  const [loading, setLoading] = useState(true);

  // Charger les derniers RDV pris
  const loadRecentRDV = useCallback(async () => {
    try {
      const resp = await authFetch(`${API}/rdv/recents?limit=10`);
      if (resp.ok) {
        const data = await resp.json();
        setRdvList(data.rdv || []);
      }
    } catch (e) {
      console.error('Erreur chargement RDV récents:', e);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadRecentRDV();
    // Rafraîchir toutes les 30 secondes
    const interval = setInterval(loadRecentRDV, 30000);
    return () => clearInterval(interval);
  }, [loadRecentRDV]);

  // Navigation
  const nextRDV = () => {
    setCurrentIndex(prev => (prev + 1) % rdvList.length);
  };
  
  const prevRDV = () => {
    setCurrentIndex(prev => (prev - 1 + rdvList.length) % rdvList.length);
  };

  // Format date relative
  const formatRelativeTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'À l\'instant';
    if (diffMins < 60) return `Il y a ${diffMins} min`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    return date.toLocaleDateString('fr-FR');
  };

  // Pas de RDV récents
  if (loading || rdvList.length === 0) {
    return null;
  }

  // Minimisé - bouton flottant
  if (isMinimized) {
    return (
      <button
        onClick={() => setIsMinimized(false)}
        className="fixed bottom-4 right-4 z-50 bg-gradient-to-r from-purple-500 to-pink-500 text-white px-4 py-3 rounded-full shadow-lg flex items-center gap-2 animate-pulse hover:animate-none hover:scale-105 transition-transform"
      >
        <PartyPopper className="w-5 h-5" />
        <span className="font-bold">{rdvList.length} R1 récent{rdvList.length > 1 ? 's' : ''}</span>
      </button>
    );
  }

  const currentRDV = rdvList[currentIndex];

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50">
      {/* Bannière principale - Design sombre */}
      <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 border-t border-purple-500/30 shadow-2xl">
        {/* Header avec compteur */}
        <div className="flex items-center justify-between px-4 py-2 bg-purple-500/10 border-b border-purple-500/20">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-purple-400 rounded-full animate-pulse" />
              <span className="text-purple-400 font-bold text-sm uppercase tracking-wider">
                Nouveau R1 Pris
              </span>
            </div>
            <span className="bg-purple-500 text-white text-xs px-2 py-0.5 rounded-full font-bold">
              {rdvList.length}
            </span>
          </div>
          
          {/* Indicateurs de défilement */}
          {rdvList.length > 1 && (
            <div className="flex gap-1">
              {rdvList.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentIndex(idx)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    idx === currentIndex ? 'bg-purple-400 w-6' : 'bg-slate-600 hover:bg-slate-500'
                  }`}
                />
              ))}
            </div>
          )}
          
          <button
            onClick={() => setIsMinimized(true)}
            className="p-1 hover:bg-slate-700 rounded"
          >
            <X className="w-4 h-4 text-slate-400" />
          </button>
        </div>

        {/* Contenu du RDV */}
        <div className="px-6 py-4">
          <div className="flex items-center justify-between gap-6">
            {/* Navigation gauche */}
            {rdvList.length > 1 && (
              <button 
                onClick={prevRDV}
                className="p-2 bg-slate-800 hover:bg-slate-700 rounded-lg transition-colors"
              >
                <ChevronLeft className="w-5 h-5 text-slate-400" />
              </button>
            )}

            {/* Info principale */}
            <div className="flex items-center gap-4 flex-1">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center shadow-lg">
                <PartyPopper className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-purple-300 text-xs font-medium uppercase tracking-wider">
                  Client
                </p>
                <p className="text-white text-xl font-bold">
                  {currentRDV.client_nom} {currentRDV.client_prenom}
                </p>
              </div>
            </div>

            {/* Infos RDV */}
            <div className="flex items-center gap-4">
              {/* Télépro */}
              <div className="flex items-center gap-2 bg-slate-800 px-4 py-2 rounded-xl">
                <User className="w-5 h-5 text-purple-400" />
                <div>
                  <p className="text-xs text-slate-400">Pris par</p>
                  <p className="text-white font-medium">{currentRDV.telepro_nom || 'Télépro'}</p>
                </div>
              </div>

              {/* Département */}
              <div className="flex items-center gap-2 bg-slate-800 px-4 py-2 rounded-xl">
                <MapPin className="w-5 h-5 text-pink-400" />
                <div>
                  <p className="text-xs text-slate-400">Département</p>
                  <p className="text-white font-bold text-lg">{currentRDV.departement || 'N/C'}</p>
                </div>
              </div>

              {/* Date RDV */}
              <div className="flex items-center gap-2 bg-slate-800 px-4 py-2 rounded-xl">
                <Calendar className="w-5 h-5 text-orange-400" />
                <div>
                  <p className="text-xs text-slate-400">Date R1</p>
                  <p className="text-white font-medium">{currentRDV.date_rdv || 'À définir'}</p>
                </div>
              </div>

              {/* Quand pris */}
              <div className="flex items-center gap-2 bg-slate-800 px-4 py-2 rounded-xl">
                <Clock className="w-5 h-5 text-emerald-400" />
                <div>
                  <p className="text-xs text-slate-400">Pris</p>
                  <p className="text-white font-medium">{formatRelativeTime(currentRDV.date_creation)}</p>
                </div>
              </div>
            </div>

            {/* Bouton voir */}
            <button
              onClick={() => onClientClick && onClientClick(currentRDV.row_number)}
              className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-bold rounded-xl transition-all hover:scale-105 shadow-lg"
            >
              Voir le client
            </button>

            {/* Navigation droite */}
            {rdvList.length > 1 && (
              <button 
                onClick={nextRDV}
                className="p-2 bg-slate-800 hover:bg-slate-700 rounded-lg transition-colors"
              >
                <ChevronRight className="w-5 h-5 text-slate-400" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
